// --------------------------------------------------
// ✅ Trekkin.app — TopControlBar.jsx v4.2.1
// --------------------------------------------------
// ✔ Alinhado com MapView (off | needle | follow)
// ✔ iOS: pede permissão via gesto (DeviceOrientationEvent)
// ✔ Android: bússola gerida APENAS pelo useGPSHybrid
// ✔ Ciclo: off → needle → follow → off
// ✔ Layout e UX INTACTOS
// ✔ Regra de Ouro cumprida
// --------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useGroupStore } from "../stores/useGroupStore";
import { useEventsStore } from "../stores/useEventsStore";
import { useAuthStore } from "../stores/useAuthStore";
import { useActivityModeStore } from "../stores/useActivityModeStore.jsx";
import { useCan } from "../hooks/useCan.js";
import { useBackgroundIdle } from "../hooks/useBackgroundIdle.js";
import { useDockPointerSelection } from "../hooks/useDockPointerSelection.js";
import { useTranslate } from "../i18n/useTranslate";
import HeartRateMapHUD from "./HeartRateMapHUD.jsx";
import { db } from "../firebaseConfig";
import { get, push, ref, remove, update, onValue, query, orderByChild, limitToLast } from "firebase/database";
import { showToast } from "./NotifyToast.jsx";
import TrekkinButton from "./TrekkinButton.jsx";
import CloseButton from "./CloseButton.jsx";
import { playChatMessage, playPing } from "../utils/sounds";
import { triggerAlertVibration } from "../utils/alertFeedback.js";
import { DEFAULT_ALERTS_CATALOG } from "../utils/alertsCatalog.js";
import { buildFaunaArchiveUpdates } from "../utils/faunaAlerts.js";
import {
  ensureAndroidForegroundLocationPermission,
  getAndroidGPSPlugin,
} from "../utils/androidLocationPermission.js";
import {
  ACTIVITY_MODE_BIKE,
  ACTIVITY_MODE_WALK,
} from "../utils/activityMode.js";

import {
  trekkinBubbleStyle,
  trekkinBubbleIcon,
} from "../styles/TrekkinPanelStyles.js";

const labeledMapBubbleStyle = {
  ...trekkinBubbleStyle,
  flexDirection: "column",
  gap: 1,
  padding: "4px 2px 3px",
  boxSizing: "border-box",
  overflow: "hidden",
  color: "#111111",
};
const labeledMapBubbleIcon = { ...trekkinBubbleIcon, width: 24, height: 24, flex: "0 0 auto" };
const mapBubbleLabelStyle = { color: "#111111", fontSize: 8.5, lineHeight: 1, fontWeight: 800, textAlign: "center", whiteSpace: "nowrap" };

import { stopNativeCompass } from "../nativeCompassBridge";

const COMPASS_PERMISSION_SESSION_KEY = "trekkin.compassPermission.session.v1";

// --------------------------------------------------
// 🧭 iOS Compass permission — só funciona em gesto
// --------------------------------------------------
async function requestCompassPermission() {
  try {
    if (window.sessionStorage.getItem(COMPASS_PERMISSION_SESSION_KEY) === "granted") {
      return true;
    }
  } catch {}

  if (
    typeof window !== "undefined" &&
    typeof DeviceOrientationEvent !== "undefined" &&
    typeof DeviceOrientationEvent.requestPermission === "function"
  ) {
    try {
      const res = await DeviceOrientationEvent.requestPermission();
      console.log("🧭 Compass permission =", res);
      try {
        if (res === "granted") {
          window.sessionStorage.setItem(COMPASS_PERMISSION_SESSION_KEY, "granted");
        } else {
          window.sessionStorage.removeItem(COMPASS_PERMISSION_SESSION_KEY);
        }
      } catch {}
      return res === "granted";
    } catch (e) {
      console.warn("🧭 Compass permission error:", e);
      return false;
    }
  }

  // Android / browsers normais
  return true;
}

export default function TopControlBar() {
  const translate = useTranslate();
  const activityMode = useActivityModeStore((state) => state.activityMode);
  const setActivityMode = useActivityModeStore((state) => state.setActivityMode);
  const {
    isFullscreen,
    toggleFullscreen,
    openPanel,
    currentPanel,
  } = usePanelsStore();

  const { activeGroupId, currentGroup, groups, joinGroup } = useGroupStore();
  const { user } = useAuthStore();
  const isAdminUser = String(user?.role || "").toLowerCase() === "admin";
  const [adminUserView, setAdminUserView] = useState(() => {
    try {
      return window.localStorage.getItem("trekkin_admin_user_view_v1") === "1";
    } catch {
      return false;
    }
  });
  const roleLower = String(user?.role || "").toLowerCase();
  const isGuideOrAdmin = roleLower === "guide" || roleLower === "admin";
  const canPlanRoute = useCan("PanelNav", "plan_route");
  const canWaypoints = useCan("PanelNav", "waypoints");
  const canManageNavGrid = useCan("PanelNavGridAdmin", "open");
  const canUsePremiumSegments = useCan("PanelNavGridAccess", "use_premium_segments");
  const canNavGrid = isAdminUser && !adminUserView && canManageNavGrid;
  useEffect(() => {
    const sync = (event) => {
      if (typeof event?.detail?.enabled === "boolean") {
        setAdminUserView(event.detail.enabled);
        return;
      }
      try {
        setAdminUserView(window.localStorage.getItem("trekkin_admin_user_view_v1") === "1");
      } catch {
        setAdminUserView(false);
      }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("trekkin:admin-user-view", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("trekkin:admin-user-view", sync);
    };
  }, []);
  const backgroundIdle = useBackgroundIdle();
  const events = useEventsStore((s) => s.events);
  const base = import.meta.env.BASE_URL || "/";
  const [mapDockOpen, setMapDockOpen] = useState(false);
  const mapDockCloseTimerRef = useRef(null);
  const mapDockOverlayOpenRef = useRef(false);
  const closeMapDock = useCallback(() => {
    if (mapDockCloseTimerRef.current) {
      window.clearTimeout(mapDockCloseTimerRef.current);
      mapDockCloseTimerRef.current = null;
    }
    setMapDockOpen(false);
    mapDockOverlayOpenRef.current = false;
    setAlertOpen(false);
    setAlertsMenuOpen(false);
  }, []);
  const resetMapDockCloseTimer = useCallback(() => {
    if (mapDockCloseTimerRef.current) {
      window.clearTimeout(mapDockCloseTimerRef.current);
    }
    mapDockCloseTimerRef.current = window.setTimeout(() => {
      mapDockCloseTimerRef.current = null;
      if (mapDockOverlayOpenRef.current) return;
      setMapDockOpen(false);
    }, 3000);
  }, []);
  const { hoveredKey: mapDockHoveredKey, dockPointerProps: mapDockPointerProps } =
    useDockPointerSelection({
      onOptionSelected: closeMapDock,
      onInteraction: resetMapDockCloseTimer,
      onTouchOptionSelected: (button) => {
        const action = button?.dataset?.dockItem;
        if (action === "compass") {
          void toggleCompassMode();
          return true;
        }
        if (action === "alerts") {
          mapDockOverlayOpenRef.current = true;
          setAlertsMenuOpen(true);
          if (canViewMapAlerts || canViewTargetedAlerts) {
            localStorage.setItem("mapAlerts.lastRead", String(Date.now()));
            setHasUnreadMapAlert(false);
          }
          return true;
        }
        return false;
      },
    });
  const mapDockItemClass = useCallback(
    (key, active = false) =>
      `tk-map-dock-item${active ? " is-active" : ""}${
        mapDockHoveredKey === key ? " is-hovered" : ""
      }`,
    [mapDockHoveredKey]
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const liveAlertMenuRef = useRef(null);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [hasUnreadAlert, setHasUnreadAlert] = useState(false);
  const lastAlertTsRef = useRef(0);
  const [alertsMenuOpen, setAlertsMenuOpen] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [hasUnreadMapAlert, setHasUnreadMapAlert] = useState(false);
  const lastMapAlertTsRef = useRef(0);
  const [alertsLayerOn, setAlertsLayerOn] = useState(() => {
    try {
      return localStorage.getItem("alertsLayerOn") === "1";
    } catch {
      return false;
    }
  });
  const [navGridOn, setNavGridOn] = useState(() => {
    try {
      return localStorage.getItem("navGridLayerOn") === "1";
    } catch {
      return false;
    }
  });
  const [protectedZonesOn, setProtectedZonesOn] = useState(() => {
    try {
      return localStorage.getItem("protectedZonesLayerOn") === "1";
    } catch {
      return false;
    }
  });
  const [navGridRecordActive, setNavGridRecordActive] = useState(
    () => typeof window !== "undefined" && window.__trekkinNavGridRecActive === true
  );
  const [waypointsLayerOn, setWaypointsLayerOn] = useState(() => {
    try {
      return localStorage.getItem("waypointsLayerOn") === "1";
    } catch {
      return false;
    }
  });
  const waypointsLongPressTimerRef = useRef(null);
  const waypointsPointerStartRef = useRef(null);
  const suppressWaypointsClickRef = useRef(false);
  const [followEnabled, setFollowEnabled] = useState(false);
  const trailPreviewPalette = ["#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c", "#0891b2"];
  const [mapOnlyTrails, setMapOnlyTrails] = useState(() =>
    Object.values(
      (typeof window !== "undefined" && window.__trekkinMapOnlyTrails) || {}
    )
  );
  const [trailClearModalOpen, setTrailClearModalOpen] = useState(false);

  useEffect(() => {
    if (!mapDockOpen) {
      if (mapDockCloseTimerRef.current) {
        window.clearTimeout(mapDockCloseTimerRef.current);
        mapDockCloseTimerRef.current = null;
      }
      return undefined;
    }
    resetMapDockCloseTimer();
    return () => {
      if (mapDockCloseTimerRef.current) {
        window.clearTimeout(mapDockCloseTimerRef.current);
        mapDockCloseTimerRef.current = null;
      }
    };
  }, [mapDockOpen, resetMapDockCloseTimer]);

  useEffect(() => {
    const overlayOpen = alertOpen || alertsMenuOpen;
    mapDockOverlayOpenRef.current = overlayOpen;
    if (overlayOpen) {
      if (mapDockCloseTimerRef.current) {
        window.clearTimeout(mapDockCloseTimerRef.current);
        mapDockCloseTimerRef.current = null;
      }
    } else if (mapDockOpen) {
      resetMapDockCloseTimer();
    }
  }, [alertOpen, alertsMenuOpen, mapDockOpen, resetMapDockCloseTimer]);

  const cancelWaypointsLongPress = useCallback(() => {
    if (waypointsLongPressTimerRef.current) {
      window.clearTimeout(waypointsLongPressTimerRef.current);
      waypointsLongPressTimerRef.current = null;
    }
    waypointsPointerStartRef.current = null;
  }, []);

  const startWaypointsLongPress = useCallback(
    (event) => {
      if (event.pointerType === "touch") return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      cancelWaypointsLongPress();
      suppressWaypointsClickRef.current = false;
      waypointsPointerStartRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      waypointsLongPressTimerRef.current = window.setTimeout(() => {
        waypointsLongPressTimerRef.current = null;
        waypointsPointerStartRef.current = null;
        suppressWaypointsClickRef.current = true;
        if (navigator.vibrate) navigator.vibrate(30);
        openPanel("waypoints", { openCategorySelector: true });
      }, 550);
    },
    [cancelWaypointsLongPress, openPanel]
  );

  const startWaypointsTouchLongPress = useCallback(
    (event) => {
      if (event.touches?.length !== 1) return;
      const touch = event.touches[0];
      cancelWaypointsLongPress();
      suppressWaypointsClickRef.current = false;
      waypointsPointerStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      waypointsLongPressTimerRef.current = window.setTimeout(() => {
        waypointsLongPressTimerRef.current = null;
        waypointsPointerStartRef.current = null;
        suppressWaypointsClickRef.current = true;
        if (navigator.vibrate) navigator.vibrate(30);
        openPanel("waypoints", { openCategorySelector: true });
      }, 550);
    },
    [cancelWaypointsLongPress, openPanel]
  );

  const moveWaypointsLongPress = useCallback(
    (event) => {
      const start = waypointsPointerStartRef.current;
      if (!start) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) {
        cancelWaypointsLongPress();
      }
    },
    [cancelWaypointsLongPress]
  );

  const moveWaypointsTouchLongPress = useCallback(
    (event) => {
      if (event.touches?.length !== 1) {
        cancelWaypointsLongPress();
        return;
      }
      const touch = event.touches[0];
      moveWaypointsLongPress({ clientX: touch.clientX, clientY: touch.clientY });
    },
    [cancelWaypointsLongPress, moveWaypointsLongPress]
  );

  useEffect(() => cancelWaypointsLongPress, [cancelWaypointsLongPress]);

  useEffect(() => {
    const syncFollowState = (event) => {
      if (typeof event?.detail?.enabled !== "boolean") return;
      setFollowEnabled(event.detail.enabled);
    };
    window.addEventListener("trekkin:follow-enabled-changed", syncFollowState);
    return () => {
      window.removeEventListener("trekkin:follow-enabled-changed", syncFollowState);
    };
  }, []);

  useEffect(() => {
    const syncRecordState = (event) => {
      setNavGridRecordActive(event?.detail?.active === true);
    };
    setNavGridRecordActive(window.__trekkinNavGridRecActive === true);
    window.addEventListener("trekkin:navgrid-record-state", syncRecordState);
    return () => {
      window.removeEventListener("trekkin:navgrid-record-state", syncRecordState);
    };
  }, []);

  useEffect(() => {
    const syncMapOnlyTrail = (event) => {
      const detail = event?.detail || {};
      if (typeof detail?.on === "boolean") {
        const trailId = String(detail?.trailId || "").trim();
        setMapOnlyTrails((current) => {
          if (!detail.on) {
            return trailId
              ? current.filter((item) => item.trailId !== trailId)
              : [];
          }
          if (!trailId) return current;
          const next = {
            trailId,
            trailName: String(detail?.trailName || "Trilho").trim() || "Trilho",
          };
          return [...current.filter((item) => item.trailId !== trailId), next];
        });
        return;
      }
      setMapOnlyTrails(Object.values(window.__trekkinMapOnlyTrails || {}));
    };
    window.addEventListener("trekkin:trail-preview-map", syncMapOnlyTrail);
    window.addEventListener("trekkin:trail-preview-map-state", syncMapOnlyTrail);
    return () => {
      window.removeEventListener("trekkin:trail-preview-map", syncMapOnlyTrail);
      window.removeEventListener("trekkin:trail-preview-map-state", syncMapOnlyTrail);
    };
  }, []);

  const clearMapOnlyTrail = useCallback((trailId = "") => {
    const id = String(trailId || "").trim();
    const active = { ...(window.__trekkinMapOnlyTrails || {}) };
    if (id) delete active[id];
    else Object.keys(active).forEach((key) => delete active[key]);
    window.__trekkinMapOnlyTrails = active;
    window.dispatchEvent(
      new CustomEvent("trekkin:trail-preview-map", {
        detail: { on: false, trailId: id, points: [] },
      })
    );
    window.dispatchEvent(
      new CustomEvent("trekkin:trail-stop-plan", {
        detail: { on: false, trailId: id },
      })
    );
    window.dispatchEvent(new CustomEvent("trekkin:trail-preview-map-state"));
    setMapOnlyTrails(Object.values(active));
    if (!Object.keys(active).length) setTrailClearModalOpen(false);
    showToast(id ? "Trilho ocultado do mapa." : "Todos os trilhos foram ocultados.", "info");
  }, []);

  useEffect(() => {
    if (!alertOpen) return undefined;

    const closeLiveAlertMenu = (event) => {
      if (liveAlertMenuRef.current?.contains(event.target)) return;
      setAlertOpen(false);
    };

    document.addEventListener("pointerdown", closeLiveAlertMenu, true);
    return () => {
      document.removeEventListener("pointerdown", closeLiveAlertMenu, true);
    };
  }, [alertOpen]);

  const ensureLocationPermissionForCenter = async () => {
    let permissionGranted = true;
    try {
      const plugin = getAndroidGPSPlugin();

      if (plugin?.checkPermissions) {
        permissionGranted = await ensureAndroidForegroundLocationPermission();
      }
    } catch (err) {
      console.warn("⚠️ CenterMap permission via plugin falhou:", err);
    }

    if (!permissionGranted) return { granted: false, position: null };

    if (Capacitor.isNativePlatform?.()) {
      try {
        const permission = await Geolocation.checkPermissions();
        const granted =
          permission?.location === "granted" ||
          permission?.coarseLocation === "granted";
        const requested = granted
          ? permission
          : await Geolocation.requestPermissions();
        const nativeGranted =
          requested?.location === "granted" ||
          requested?.coarseLocation === "granted";
        if (!nativeGranted) return { granted: false, position: null };

        const result = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
        const latitude = Number(result?.coords?.latitude);
        const longitude = Number(result?.coords?.longitude);
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          return {
            granted: true,
            position: {
              lat: latitude,
              lon: longitude,
              ele: Number(result?.coords?.altitude) || 0,
              acc: Number(result?.coords?.accuracy) || 0,
              spd: Number(result?.coords?.speed) || 0,
              ts: Number(result?.timestamp) || Date.now(),
              source: "center-button-native",
            },
          };
        }
      } catch (err) {
        console.warn("⚠️ CenterMap posição nativa falhou; a tentar WebView:", err);
      }
    }

    if (!("geolocation" in navigator)) {
      return { granted: true, position: null };
    }

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (result) => {
          const latitude = Number(result?.coords?.latitude);
          const longitude = Number(result?.coords?.longitude);
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            resolve({ granted: true, position: null });
            return;
          }
          resolve({
            granted: true,
            position: {
              lat: latitude,
              lon: longitude,
              ele: Number(result?.coords?.altitude) || 0,
              acc: Number(result?.coords?.accuracy) || 0,
              spd: Number(result?.coords?.speed) || 0,
              ts: Number(result?.timestamp) || Date.now(),
              source: "center-button",
            },
          });
        },
        (error) => {
          const denied = Number(error?.code) === 1;
          resolve({ granted: !denied, position: null });
        },
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    });
  };

  // --------------------------------------------------
  // 🧭 Compass Mode (espelha MapView)
  // off | needle | follow
  // --------------------------------------------------
  const [compassMode, setCompassMode] = useState("off");
  const canOpenGuidePanel = useCan("PanelGuide", "open");

  const shouldHide = !!currentPanel?.name;
  const uid = user?.uid || null;
  const groupsArr = Array.isArray(groups)
    ? groups
    : groups && typeof groups === "object"
      ? Object.values(groups)
      : [];

  const [chatUnreadGlobal, setChatUnreadGlobal] = useState(false);
  const [chatUnreadGroups, setChatUnreadGroups] = useState({});
  const [unreadDirectChats, setUnreadDirectChats] = useState([]);
  const [pendingGuideRequestCount, setPendingGuideRequestCount] = useState(0);
  const [unreadChatPickerOpen, setUnreadChatPickerOpen] = useState(false);
  const [lastGlobalMessageTs, setLastGlobalMessageTs] = useState(0);
  const [lastGroupMessageTs, setLastGroupMessageTs] = useState({});
  const lastGlobalMessageTsRef = useRef(0);
  const lastGroupMessageTsRef = useRef({});
  const lastChatUnreadCountRef = useRef(null);

  const memberGroupIds = useMemo(() => {
    if (!uid) return [];
    const ids = groupsArr
      .filter((g) => !!g?.members?.[uid])
      .map((g) => g?.id || g?.groupId || g?.firebaseId || g?.key || null)
      .filter(Boolean);
    return Array.from(new Set(ids)).sort();
  }, [groupsArr, uid]);
  const memberGroupIdsKey = memberGroupIds.join("|");

  const recomputeUnread = useCallback(
    (nextGlobalTs = lastGlobalMessageTsRef.current, nextGroupTs = lastGroupMessageTsRef.current) => {
      const globalKey = "globalChat.lastOpened";
      const globalRaw = localStorage.getItem(globalKey);
      let globalLastRead = Number(globalRaw || 0);
      const hasGlobalUnread = Number(nextGlobalTs || 0) > globalLastRead;

      const byGroup = {};
      for (const gid of memberGroupIds) {
        const readKey = `groupChat.${gid}.lastRead`;
        const readRaw = localStorage.getItem(readKey);
        const lastTs = Number(nextGroupTs?.[gid] || 0);
        let lastRead = Number(readRaw || 0);
        if (!readRaw && lastTs > 0) {
          lastRead = lastTs;
          try {
            localStorage.setItem(readKey, String(lastRead));
          } catch {}
        }
        byGroup[gid] = lastTs > lastRead;
      }

      setChatUnreadGlobal(hasGlobalUnread);
      setChatUnreadGroups(byGroup);
    },
    [memberGroupIdsKey]
  );

  useEffect(() => {
    if (backgroundIdle) return;
    const q = query(ref(db, "globalChat/messages"), limitToLast(1));
    const unsub = onValue(q, (snap) => {
      let msg = null;
      snap.forEach((c) => {
        msg = c.val() || null;
      });
      const ts = Number(msg?.ts || msg?.timestamp || 0);
      const senderId = msg?.uid || msg?.from || msg?.fromId || msg?.senderId || null;
      const openChat = usePanelsStore.getState().currentPanel;
      const isOpen = openChat?.name === "globalChat";
      if (ts && (String(senderId || "") === String(uid || "") || isOpen)) {
        try {
          localStorage.setItem("globalChat.lastOpened", String(ts));
        } catch {}
      }
      lastGlobalMessageTsRef.current = ts;
      setLastGlobalMessageTs(ts);
      recomputeUnread(ts, lastGroupMessageTsRef.current);
    });

    return () => unsub();
  }, [backgroundIdle, recomputeUnread, uid]);

  useEffect(() => {
    let categories = [];
    let categoryPalette = {};
    try {
      const rawCats = localStorage.getItem("waypointsSelectedCategories");
      const parsedCats = rawCats ? JSON.parse(rawCats) : [];
      categories = Array.isArray(parsedCats) ? parsedCats : [];
    } catch {}
    try {
      const rawPalette = localStorage.getItem("waypointsCategoryPalette");
      const parsedPalette = rawPalette ? JSON.parse(rawPalette) : {};
      categoryPalette =
        parsedPalette && typeof parsedPalette === "object" ? parsedPalette : {};
    } catch {}
    const timer = window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("trekkin:waypoints-map-filter", {
          detail: { on: waypointsLayerOn, categories, categoryPalette },
        })
      );
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const explicit = event?.detail?.on;
      if (typeof explicit === "boolean") {
        setWaypointsLayerOn(explicit);
      }
    };
    window.addEventListener("trekkin:waypoints-map-filter", handler);
    const onStorage = (event) => {
      if (event?.key !== "waypointsLayerOn") return;
      setWaypointsLayerOn(String(event?.newValue || "") === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("trekkin:waypoints-map-filter", handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (backgroundIdle) return;
    if (!memberGroupIds.length) {
      lastGroupMessageTsRef.current = {};
      setLastGroupMessageTs({});
      setChatUnreadGroups({});
      return;
    }

    const latestByPath = {};
    const publishLatest = (gid) => {
      const candidates = Object.values(latestByPath[gid] || {});
      const latest = candidates.reduce(
        (best, candidate) => Number(candidate?.ts || 0) > Number(best?.ts || 0) ? candidate : best,
        null
      );
      const lastTs = Number(latest?.ts || 0);
      const openChat = usePanelsStore.getState().currentPanel;
      const openGroupId = openChat?.props?.groupId;
      const isOpen =
        openChat?.name === "groupChat" && String(openGroupId || "") === String(gid);
      if (lastTs && (String(latest?.senderId || "") === String(uid || "") || isOpen)) {
        try {
          localStorage.setItem(`groupChat.${gid}.lastRead`, String(lastTs));
        } catch {}
      }
      setLastGroupMessageTs((prev) => {
        const next = { ...prev, [gid]: lastTs };
        lastGroupMessageTsRef.current = next;
        recomputeUnread(lastGlobalMessageTsRef.current, next);
        return next;
      });
    };

    const unsubs = memberGroupIds.flatMap((gid) => {
      latestByPath[gid] = {
        current: { ts: 0, senderId: null },
        legacy: { ts: 0, senderId: null },
      };
      return [
        ["current", `groupChats/${gid}/messages`],
        ["legacy", `groupChat/${gid}/messages`],
      ].map(([source, path]) => {
        const q = query(ref(db, path), limitToLast(1));
        return onValue(q, (snap) => {
          let latestMessage = null;
          snap.forEach((c) => {
            const v = c.val() || {};
            if (Number(v.ts || v.timestamp || 0) >= Number(latestMessage?.ts || latestMessage?.timestamp || 0)) {
              latestMessage = v;
            }
          });
          latestByPath[gid][source] = {
            ts: Number(latestMessage?.ts || latestMessage?.timestamp || 0),
            senderId:
              latestMessage?.uid ||
              latestMessage?.from ||
              latestMessage?.fromId ||
              latestMessage?.senderId ||
              null,
          };
          publishLatest(gid);
        });
      });
    });

    return () => {
      unsubs.forEach((u) => u && u());
    };
  }, [backgroundIdle, memberGroupIdsKey, recomputeUnread, uid]);

  useEffect(() => {
    const onRead = () => recomputeUnread();
    window.addEventListener("trekkin:chat-read-updated", onRead);
    window.addEventListener("storage", onRead);
    return () => {
      window.removeEventListener("trekkin:chat-read-updated", onRead);
      window.removeEventListener("storage", onRead);
    };
  }, [recomputeUnread]);

  useEffect(() => {
    if (!uid) {
      setUnreadDirectChats([]);
      return;
    }
    if (backgroundIdle) return;

    const inboxes = { user: {}, guide: {} };
    let rebuildVersion = 0;
    let disposed = false;

    const rebuild = async () => {
      const version = ++rebuildVersion;
      const merged = new Map();

      Object.entries(inboxes).forEach(([inboxType, entries]) => {
        Object.entries(entries || {}).forEach(([chatId, rawMeta]) => {
          const meta = rawMeta && typeof rawMeta === "object" ? rawMeta : {};
          if (
            !chatId ||
            meta.unread !== true ||
            meta.archived === true ||
            meta.ended === true
          ) return;
          const previous = merged.get(chatId);
          const ts = Number(meta.lastMessageAt || meta.ts || 0);
          const previousTs = Number(previous?.meta?.lastMessageAt || previous?.meta?.ts || 0);
          if (!previous || ts >= previousTs) merged.set(chatId, { chatId, inboxType, meta });
        });
      });

      const resolved = await Promise.all(
        Array.from(merged.values()).map(async ({ chatId, inboxType, meta }) => {
          let chat = {};
          try {
            chat = (await get(ref(db, `directChats/${chatId}`))).val() || {};
          } catch {}
          if (chat.ended === true) return null;

          const otherUid = String(chat.userId || "") === String(uid)
            ? chat.guideId
            : chat.userId;
          let peer = {};
          if (otherUid) {
            try {
              peer = (await get(ref(db, `usersPublic/${otherUid}`))).val() || {};
            } catch {}
            if (!peer?.name && !peer?.displayName && !peer?.username) {
              try {
                peer = (await get(ref(db, `users/${otherUid}`))).val() || {};
              } catch {}
            }
          }

          const peerName =
            peer?.name || peer?.displayName || peer?.username || meta?.userName || meta?.guideName;
          return {
            type: "direct",
            id: chatId,
            inboxType,
            label: peerName ? `Chat com ${peerName}` : "Chat direto",
            subtitle: meta?.eventName || chat?.eventName || "",
            ts: Number(chat?.lastMessageAt || meta?.lastMessageAt || meta?.ts || 0),
          };
        })
      );

      if (!disposed && version === rebuildVersion) {
        setUnreadDirectChats(resolved.filter(Boolean));
      }
    };

    const unsubs = ["user", "guide"].map((inboxType) => {
      const path = inboxType === "guide" ? "guideDirectChats" : "userDirectChats";
      return onValue(
        ref(db, `${path}/${uid}`),
        (snap) => {
          inboxes[inboxType] = snap.val() || {};
          rebuild();
        },
        () => {
          inboxes[inboxType] = {};
          rebuild();
        }
      );
    });

    return () => {
      disposed = true;
      unsubs.forEach((unsub) => unsub?.());
    };
  }, [backgroundIdle, uid]);

  useEffect(() => {
    if (!isAdminUser) {
      setPendingGuideRequestCount(0);
      return;
    }
    if (backgroundIdle) return;
    return onValue(ref(db, "guideOnlineRequests"), (snap) => {
      let count = 0;
      snap.forEach((requestSnap) => {
        const request = requestSnap.val() || {};
        if (
          String(request.status || "") === "requested" &&
          ["guide_online", "trail_proposal"].includes(String(request.type || ""))
        ) count += 1;
      });
      setPendingGuideRequestCount(count);
    }, () => setPendingGuideRequestCount(0));
  }, [backgroundIdle, isAdminUser]);

  const hasUnread =
    chatUnreadGlobal ||
    Object.values(chatUnreadGroups).some(Boolean) ||
    unreadDirectChats.length > 0;
  const chatUnreadCount =
    (chatUnreadGlobal ? 1 : 0) +
    Object.values(chatUnreadGroups).filter(Boolean).length +
    unreadDirectChats.length;
  const appBadgeCount = chatUnreadCount + pendingGuideRequestCount;

  useEffect(() => {
    // O badge do ícone serve para chamar a atenção quando a app não está a ser
    // usada. Com a app visível, os próprios indicadores do chat/alertas mostram
    // os pendentes e o badge nativo deve ficar limpo.
    const nativeBadgeCount = document.visibilityState === "visible" ? 0 : appBadgeCount;
    try {
      window?.webkit?.messageHandlers?.nativeBridge?.postMessage({
        action: "setAppBadge",
        count: nativeBadgeCount,
      });
      window?.Capacitor?.Plugins?.Badge?.set?.({ count: nativeBadgeCount }).catch?.(() => {});
    } catch {}

    const previous = lastChatUnreadCountRef.current;
    lastChatUnreadCountRef.current = appBadgeCount;
    if (previous === null || appBadgeCount <= previous) return;
    let enabled = true;
    let sound = true;
    let vibrate = true;
    try {
      enabled = localStorage.getItem("chatAlertsEnabled") !== "0";
      sound = localStorage.getItem("autoAlertSound") !== "0";
      vibrate = localStorage.getItem("autoAlertVibrate") !== "0";
    } catch {}
    if (!enabled) return;
    if (sound) playChatMessage();
    if (vibrate) triggerAlertVibration([120, 70, 120]);
  }, [appBadgeCount]);

  const unreadChatOptions = useMemo(() => {
    const options = memberGroupIds
      .filter((gid) => !!chatUnreadGroups?.[gid])
      .map((gid) => {
        const group = groupsArr.find((candidate) => {
          const candidateId =
            candidate?.id || candidate?.groupId || candidate?.firebaseId || candidate?.key;
          return String(candidateId || "") === String(gid);
        });
        return {
          type: "group",
          id: gid,
          label: group?.name || group?.title || group?.groupName || `Grupo ${gid}`,
          ts: Number(lastGroupMessageTs?.[gid] || 0),
        };
      });

    if (chatUnreadGlobal) {
      options.push({
        type: "global",
        id: "global",
        label: translate("guide.globalChat.title"),
        ts: Number(lastGlobalMessageTs || 0),
      });
    }

    return [...unreadDirectChats, ...options].sort((a, b) => b.ts - a.ts);
  }, [
    chatUnreadGlobal,
    chatUnreadGroups,
    groupsArr,
    lastGlobalMessageTs,
    lastGroupMessageTs,
    memberGroupIdsKey,
    translate,
    unreadDirectChats,
  ]);

  const openUnreadChat = useCallback(
    async (option) => {
      setUnreadChatPickerOpen(false);
      if (option?.type === "global") {
        openPanel("globalChat");
        return;
      }
      if (option?.type === "direct") {
        const inboxPath =
          option.inboxType === "guide" ? "guideDirectChats" : "userDirectChats";
        try {
          await update(ref(db, `${inboxPath}/${uid}/${option.id}`), { unread: false });
        } catch {}
        openPanel("directChat", { chatId: option.id });
        return;
      }
      if (!option?.id) return;
      try {
        await joinGroup?.(option.id);
      } catch {}
      openPanel("groupChat", { groupId: option.id });
    },
    [joinGroup, openPanel, uid]
  );

  const handleOpenMessages = async () => {
    if (unreadChatOptions.length > 1) {
      setUnreadChatPickerOpen(true);
      return;
    }

    if (unreadChatOptions.length === 1) {
      await openUnreadChat(unreadChatOptions[0]);
      return;
    }

    // Sem mensagens novas: manter o comportamento habitual.
    if (canOpenGuidePanel) {
      openPanel("guide");
      return;
    }
    openPanel(activeGroupId ? "groupChat" : "globalChat");
  };

  const groupId =
    typeof currentGroup === "string"
      ? currentGroup
      : currentGroup?.id ||
        currentGroup?.groupId ||
        currentGroup?.firebaseId ||
        currentGroup?.key ||
        null;

  const eventsArr = Array.isArray(events)
    ? events
    : events && typeof events === "object"
      ? Object.values(events)
      : [];

  const activeGroup =
    groupId &&
    groupsArr.find((g) => {
      const gid = g?.id || g?.groupId || g?.firebaseId || g?.key || null;
      return String(gid || "") === String(groupId || "");
    });

  const activeEventId =
    activeGroup?.activeEventId || activeGroup?.activeEvent || activeGroup?.eventId || null;

  const activeEvent =
    activeEventId && eventsArr.length
      ? eventsArr.find((ev) => ev.id === activeEventId) || null
      : null;

  const isLive = !!activeEvent?.isLive;
  const isMember = !!activeGroup?.members?.[uid];
  const isGuide =
    uid &&
    activeEvent &&
    (activeEvent.guideId === uid ||
      activeEvent.guideUid === uid ||
      activeEvent.guide === uid ||
      activeEvent.coGuideId === uid ||
      activeEvent.coGuideUid === uid);
  const canAlertGuide = isLive && isMember && !isGuide;
  const canViewAlerts = isLive && isGuide && activeEventId;
  const canViewFaunaAlerts = useCan("PanelAlerts", "view_alerts_fauna");
  const canViewTrailAlerts = useCan("PanelAlerts", "view_alerts_trilho");
  const canViewSafetyAlerts = useCan("PanelAlerts", "view_alerts_seguranca");
  const canViewCriticalAlerts = useCan("PanelAlerts", "view_alerts_criticos");
  const canAddFaunaAlerts = useCan("PanelAlerts", "add_alerts_fauna");
  const canAddTrailAlerts = useCan("PanelAlerts", "add_alerts_trilho");
  const canAddSafetyAlerts = useCan("PanelAlerts", "add_alerts_seguranca");
  const canAddCriticalAlerts = useCan("PanelAlerts", "add_alerts_criticos");
  const canManageAlerts = useCan("PanelAlerts", "manage_fauna_history");
  const canViewMapAlerts =
    canViewFaunaAlerts ||
    canViewTrailAlerts ||
    canViewSafetyAlerts ||
    canViewCriticalAlerts;
  const canViewTargetedAlerts = !!uid;
  const canAddAlerts =
    canAddFaunaAlerts ||
    canAddTrailAlerts ||
    canAddSafetyAlerts ||
    canAddCriticalAlerts;
  const canUseAlertsMenu =
    canViewMapAlerts || canViewTargetedAlerts || canAddAlerts || (isAdminUser && canManageAlerts);
  const criticalTypeLabelMap = useMemo(
    () =>
      (DEFAULT_ALERTS_CATALOG.types.criticos || []).reduce((acc, item) => {
        acc[item.id] = item.label;
        return acc;
      }, {}),
    []
  );
  const normalizeCriticalType = useCallback((rawType, rawAlert = null) => {
    const type = String(rawType || "").trim().toLowerCase();
    if (type !== "admin_event_registration") return type;
    const sub = String(rawAlert?.sub || "").trim().toLowerCase();
    if (sub === "inscricao_paga") return "pagamento_recebido";
    if (sub === "inscricao_nova") return "nova_inscricao";
    const note = String(rawAlert?.note || "").trim().toLowerCase();
    if (note.includes("pagamento")) return "pagamento_recebido";
    return "nova_inscricao";
  }, []);
  const isLowQualityCriticalAlert = useCallback(
    (a) => {
      const normalizedType = normalizeCriticalType(a?.type, a);
      if (
        normalizedType !== "nova_inscricao" &&
        normalizedType !== "pagamento_recebido" &&
        normalizedType !== "pagamento_subscricao"
      ) {
        return false;
      }
      const label = String(a?.label || "").trim();
      const detail = String(a?.detail || "").trim();
      const note = String(a?.note || "").trim();
      const userName = String(a?.userName || "").trim();
      const userId = String(a?.userId || "").trim();
      const eventName = String(a?.eventName || "").trim();
      const eventId = String(a?.eventId || "").trim();
      return !label && !detail && !note && !userName && !userId && !eventName && !eventId;
    },
    [normalizeCriticalType]
  );
  const getAlertLabel = useCallback(
    (a) => {
      if (a?.label) return a.label;
      const normalizedType = normalizeCriticalType(a?.type, a);
      if (a?.type) {
        const mapped = criticalTypeLabelMap[normalizedType];
        if (mapped) return mapped;
        return String(normalizedType)
          .replace(/_/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      }
      return a?.type || "Alerta";
    },
    [criticalTypeLabelMap, normalizeCriticalType]
  );
  const buildAlertMailto = useCallback(
    (a) => {
      const categoryMap = {
        fauna: "Fauna",
        trilho: "Trilho",
        seguranca: "Segurança",
        criticos: "Críticos",
      };
      const categoryKey = String(a?.category || "").toLowerCase();
      const categoryLabel = categoryMap[categoryKey] || (a?.category || "—");
      const typeLabel = getAlertLabel(a);
      const coords =
        Number.isFinite(Number(a?.coords?.lat)) && Number.isFinite(Number(a?.coords?.lon))
          ? `${Number(a.coords.lat).toFixed(6)}, ${Number(a.coords.lon).toFixed(6)}`
          : "—";
      const subject = `Alerta Trekkin — ${categoryLabel} / ${typeLabel}`;
      const lines = [
        `Categoria: ${categoryLabel}`,
        `Tipo: ${typeLabel}`,
        `Subcategoria: ${a?.detail || a?.animal || "—"}`,
        `Texto: ${a?.note || "—"}`,
        `Coordenadas: ${coords}`,
        `Utilizador: ${a?.userName || a?.userId || "—"}`,
        `Evento: ${a?.eventName || a?.eventId || "—"}`,
        `Data: ${a?.ts ? new Date(a.ts).toLocaleString() : "—"}`,
        `Imagem: ${a?.imageUrl || "—"}`,
      ];
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
    },
    [getAlertLabel]
  );
  const handleSendAlertMail = useCallback(
    (alertEntry) => {
      try {
        window.location.href = buildAlertMailto(alertEntry);
      } catch {
        showToast("Falha ao abrir a app de email.", "error");
      }
    },
    [buildAlertMailto]
  );
  const handleDeleteRecentAlert = useCallback(async (alertEntry) => {
    if (!alertEntry?.id || !alertEntry?.category) return;
    setRecentAlerts((current) =>
      current.filter(
        (entry) =>
          !(entry?.id === alertEntry.id && entry?.category === alertEntry.category)
      )
    );
    try {
      if (String(alertEntry.category).toLowerCase() === "fauna") {
        const archivedAt = Date.now();
        await update(ref(db), buildFaunaArchiveUpdates(alertEntry, archivedAt));
        showToast("Alerta removido do mapa e preservado no histórico de Fauna.", "success");
      } else {
        await remove(ref(db, `alerts/${alertEntry.category}/${alertEntry.id}`));
        showToast("Alerta eliminado.", "success");
      }
    } catch (error) {
      console.warn("Erro ao eliminar alerta:", error);
      setRecentAlerts((current) =>
        [...current, alertEntry]
          .sort((a, b) => Number(a?.ts || 0) - Number(b?.ts || 0))
          .slice(-12)
      );
      showToast("Não foi possível eliminar o alerta.", "error");
    }
  }, []);
  const isBadCriticalAlert = (a) => {
    if (!a || typeof a !== "object") return true;
    const type = String(a.type || "").trim();
    const label = String(a.label || "").trim();
    const detail = String(a.detail || "").trim();
    const note = String(a.note || "").trim();
    const userName = String(a.userName || "").trim();
    const userId = String(a.userId || "").trim();
    const eventName = String(a.eventName || "").trim();
    const eventId = String(a.eventId || "").trim();
    if (type !== "admin_event_registration" && type !== "pagamento_subscricao") return false;
    return !label && !detail && !note && !userName && !userId && !eventName && !eventId;
  };

  const dedupAlertList = useCallback((list) => {
    const arr = Array.isArray(list) ? list : [];
    const richByTypeTs = new Set();
    arr.forEach((a) => {
      const normalizedType = normalizeCriticalType(a?.type, a);
      if (
        normalizedType !== "nova_inscricao" &&
        normalizedType !== "pagamento_recebido" &&
        normalizedType !== "pagamento_subscricao"
      ) {
        return;
      }
      if (isLowQualityCriticalAlert(a)) return;
      const ts = Number(a?.ts || 0) || 0;
      if (!ts) return;
      richByTypeTs.add(`${normalizedType}|${ts}`);
    });

    const seen = new Set();
    return arr.filter((a) => {
      const normalizedType = normalizeCriticalType(a?.type, a);
      const ts = Number(a?.ts || 0) || 0;
      if (
        isLowQualityCriticalAlert(a) &&
        ts &&
        richByTypeTs.has(`${normalizedType}|${ts}`)
      ) {
        return false;
      }
      const key = [
        String(a?.category || "").trim().toLowerCase(),
        normalizedType,
        ts,
        String(a?.eventId || "").trim().toLowerCase(),
        String(a?.userId || a?.userName || "").trim().toLowerCase(),
        String(a?.note || "").trim().toLowerCase(),
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [isLowQualityCriticalAlert, normalizeCriticalType]);

  useEffect(() => {
    if (backgroundIdle) return;
    if (!canViewAlerts || shouldHide) return;
    const eventId = activeEventId;
    const q = query(ref(db, `events/${eventId}/alerts`), orderByChild("ts"), limitToLast(20));
    const unsub = onValue(q, (snap) => {
      const raw = snap.val() || {};
      const arr = Object.values(raw)
        .filter((a) => a && typeof a === "object")
        .sort((a, b) => (a.ts || 0) - (b.ts || 0));
      setLiveAlerts(arr);

      const lastTs = arr.length ? (arr[arr.length - 1].ts || 0) : 0;
      const lastReadKey = `liveAlerts.${eventId}.lastRead`;
      const lastReadRaw = localStorage.getItem(lastReadKey);
      let lastRead = Number(lastReadRaw || 0);
      if (!lastReadRaw && lastTs) {
        try {
          localStorage.setItem(lastReadKey, String(lastTs));
        } catch {}
        lastRead = lastTs;
      }
      setHasUnreadAlert(lastTs > lastRead);

      if (lastTs && lastTs > lastAlertTsRef.current) {
        lastAlertTsRef.current = lastTs;
        playPing();
        triggerAlertVibration([200, 80, 200]);
      }
    });

    return () => unsub();
  }, [backgroundIdle, canViewAlerts, activeEventId, shouldHide]);

  useEffect(() => {
    if (backgroundIdle) return;
    if ((!canViewMapAlerts && !canViewTargetedAlerts) || shouldHide) return;

    const categories = [];
    if (canViewFaunaAlerts) categories.push("fauna");
    if (canViewTrailAlerts) categories.push("trilho");
    if (canViewSafetyAlerts) categories.push("seguranca");
    if (canViewCriticalAlerts || canViewTargetedAlerts) categories.push("criticos");

    if (!categories.length) return;

    const latestByCategory = {};
    const unsubs = categories.map((category) => {
      const q = query(
        ref(db, `alerts/${category}`),
        orderByChild("ts"),
        limitToLast(category === "criticos" && !canViewCriticalAlerts ? 300 : 12)
      );

      return onValue(q, (snap) => {
        const raw = snap.val() || {};
        const now = Date.now();
        latestByCategory[category] = Object.entries(raw).map(([id, a]) => ({
          ...(a || {}),
          id,
        }))
          .filter((a) => a && typeof a === "object")
          .filter((a) => !(a?.adminOnly && !isAdminUser))
          .filter((a) => category !== "fauna" || !a?.status || a.status === "active")
          .filter((a) => !a?.targetUid || String(a.targetUid) === String(uid || ""))
          .filter(
            (a) =>
              category !== "criticos" ||
              canViewCriticalAlerts ||
              String(a?.targetUid || "") === String(uid || "")
          )
          .filter((a) => !isBadCriticalAlert(a))
          .map((a) => {
            if (a?.persistent) return { ...a, category };
            const exp = a?.expiresAt || (a?.ts ? a.ts + 48 * 60 * 60 * 1000 : 0);
            if (exp && now > exp) return null;
            return { ...a, category };
          })
          .filter(Boolean);

        const merged = Object.values(latestByCategory)
          .flat()
          .sort((a, b) => (a.ts || 0) - (b.ts || 0))
          .slice(-24);

        const deduped = dedupAlertList(merged).slice(-12);

        setRecentAlerts(deduped);

        const lastTs = deduped.length ? (deduped[deduped.length - 1].ts || 0) : 0;
        const lastReadKey = "mapAlerts.lastRead";
        const lastReadRaw = localStorage.getItem(lastReadKey);
        let lastRead = Number(lastReadRaw || 0);
        if (!lastReadRaw && lastTs) {
          try {
            localStorage.setItem(lastReadKey, String(lastTs));
          } catch {}
          lastRead = lastTs;
        }
        setHasUnreadMapAlert(lastTs > lastRead);

        if (lastTs && lastTs > lastMapAlertTsRef.current) {
          lastMapAlertTsRef.current = lastTs;
        }
      });
    });

    return () => unsubs.forEach((u) => u && u());
  }, [
    backgroundIdle,
    canViewMapAlerts,
    canViewTargetedAlerts,
    isAdminUser,
    canViewFaunaAlerts,
    canViewTrailAlerts,
    canViewSafetyAlerts,
    canViewCriticalAlerts,
    uid,
    shouldHide,
    dedupAlertList,
  ]);

  useEffect(() => {
    const handler = (ev) => {
      if (typeof ev?.detail?.on === "boolean") {
        setAlertsLayerOn(ev.detail.on);
        return;
      }
      try {
        setAlertsLayerOn(localStorage.getItem("alertsLayerOn") === "1");
      } catch {}
    };
    window.addEventListener("trekkin:alerts-layer-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("trekkin:alerts-layer-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  async function sendLiveAlert(label, toastMsg) {
    if (!uid || !activeEventId || !groupId) return;
    const payload = {
      type: "live_alert",
      label,
      uid,
      name: user?.name || user?.email || "Trekker",
      eventId: activeEventId,
      groupId,
      ts: Date.now(),
    };

    try {
      await push(ref(db, `events/${activeEventId}/alerts`), payload);
      await push(ref(db, `groups/${groupId}/alerts`), payload);
      showToast(toastMsg);
    } catch (e) {
      console.warn("⚠️ Falha ao enviar alerta ao guia:", e);
      showToast("Falha ao enviar alerta. Verifica a internet.");
    } finally {
      setAlertOpen(false);
    }
  }

  function handleViewMapAlerts() {
    window.dispatchEvent(new CustomEvent("trekkin:alerts-view-map"));
    setAlertsMenuOpen(false);
  }

  function handleCreateAlert() {
    openPanel("alertsCreate");
    setAlertsMenuOpen(false);
  }

  function handleOpenAlertsPanel() {
    openPanel("alertsAdmin");
    setAlertsMenuOpen(false);
  }

  // --------------------------------------------------
  // 🧭 TOGGLE COMPASS MODE
  // off → needle → follow → off
  // --------------------------------------------------
  async function toggleCompassMode() {
    const next =
      compassMode === "off"
        ? "needle"
        : compassMode === "needle"
        ? "follow"
        : "off";

    // iOS → pedir permissão APENAS quando necessário
    if (next !== "off") {
      const ok = await requestCompassPermission();
      if (!ok) {
        alert("Permissão da bússola recusada");
        return;
      }

      // desbloquear eventos iOS
      window.dispatchEvent(
        new Event("trekkin:compass-permission-granted")
      );
    }

    // Avisar MapView (fonte única de estado visual)
    window.dispatchEvent(
      new CustomEvent("trekkin:set-compass-mode", {
        detail: { mode: next },
      })
    );

    // Ao entrar em qualquer modo de bússola, centra uma vez imediatamente.
    if (next !== "off") {
      window.dispatchEvent(new CustomEvent("trekkin:center-map"));
    }

    // Sair completamente → parar bússola nativa
    if (next === "off") {
      stopNativeCompass();
    }

    setCompassMode(next);
  }

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  if (shouldHide) return null;

  return (
    <div
      className={`tk-top-control-bar tk-map-dock ${mapDockOpen ? "is-open" : "is-closed"}`}
      {...mapDockPointerProps}
      onContextMenuCapture={(event) => {
        if (event.target?.closest?.("img")) event.preventDefault();
      }}
      onDragStartCapture={(event) => {
        if (event.target?.closest?.("img")) event.preventDefault();
      }}
      style={{
        position: "fixed",
        top:
          "calc(max(env(safe-area-inset-top, 0px), var(--tk-native-safe-top, 0px)) + 5px)",
        right: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        zIndex: 950,
        pointerEvents: "auto",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
      }}
    >
      {/* O mapa é o puxador do menu lateral; não altera a safe area nem o ecrã inteiro. */}
      <button
        type="button"
        className={`tk-map-dock-toggle${
          hasUnread || hasUnreadAlert || hasUnreadMapAlert ? " has-attention" : ""
        }`}
        data-dock-toggle="true"
        aria-expanded={mapDockOpen}
        aria-label={mapDockOpen ? "Fechar menu do mapa" : "Abrir menu do mapa"}
        onClick={() => {
          if (isFullscreen) toggleFullscreen();
          setAlertOpen(false);
          setAlertsMenuOpen(false);
          setMapDockOpen((open) => !open);
        }}
        style={labeledMapBubbleStyle}
      >
        <img
          src={`${base}icons/map.svg`}
          alt=""
          style={labeledMapBubbleIcon}
        />
        <span style={mapBubbleLabelStyle}>Mapa</span>
      </button>

      {/* 🚶/🚲 MODO ATIVO DA APP */}
      {mapDockOpen && !isFullscreen && (
        <button
          type="button"
          data-dock-item="mode"
          className={mapDockItemClass("mode", true)}
          onClick={() => {
            const nextMode =
              activityMode === ACTIVITY_MODE_BIKE
                ? ACTIVITY_MODE_WALK
                : ACTIVITY_MODE_BIKE;
            setActivityMode(nextMode);
            showToast(
              `${translate("topBar.mode")}: ${translate(
                nextMode === ACTIVITY_MODE_BIKE
                  ? "nav.recording.bike"
                  : "nav.recording.walk"
              )}`,
              "success"
            );
          }}
          aria-label={`${translate("topBar.mode")}: ${translate(
            activityMode === ACTIVITY_MODE_BIKE
              ? "nav.recording.bike"
              : "nav.recording.walk"
          )}`}
          title={`${translate("topBar.mode")}: ${translate(
            activityMode === ACTIVITY_MODE_BIKE
              ? "nav.recording.bike"
              : "nav.recording.walk"
          )}`}
          style={{
            ...labeledMapBubbleStyle,
            background: "rgba(40,167,69,0.25)",
            boxShadow: "0 0 0 2px rgba(40,167,69,0.35)",
          }}
        >
          <img
            src={`${base}icons/${
              activityMode === ACTIVITY_MODE_BIKE ? "bicycle.svg" : "boot.svg"
            }`}
            alt=""
            aria-hidden="true"
            style={labeledMapBubbleIcon}
          />
          <span style={mapBubbleLabelStyle}>{translate("topBar.mode")}</span>
        </button>
      )}

      {/* 🧭 BÚSSOLA */}
      {mapDockOpen && !isFullscreen && (
        <button
          type="button"
          data-dock-item="compass"
          className={mapDockItemClass("compass", compassMode !== "off")}
          onClick={toggleCompassMode}
          style={{
            ...labeledMapBubbleStyle,
            background:
              compassMode === "follow"
                ? "rgba(40,167,69,0.45)"
                : compassMode === "needle"
                ? "rgba(40,167,69,0.25)"
                : trekkinBubbleStyle.background,
            boxShadow:
              compassMode !== "off"
                ? "0 0 0 2px rgba(40,167,69,0.35)"
                : trekkinBubbleStyle.boxShadow,
            transition: "all 0.25s ease",
          }}
        >
          <img
            src={`${base}icons/compass-unlock.svg`}
            alt="Bússola"
            style={{
              ...labeledMapBubbleIcon,
              filter:
                compassMode !== "off"
                  ? "drop-shadow(0 0 6px #28a745)"
                  : "none",
            }}
          />
          <span style={mapBubbleLabelStyle}>{translate("topBar.compass")}</span>
        </button>
      )}

      {/* 🚨 ALERTA LIVE */}
      {mapDockOpen && !isFullscreen && (canAlertGuide || canViewAlerts) && (
        <div ref={liveAlertMenuRef} style={{ position: "relative" }}>
          <button
            type="button"
            data-dock-item="help"
            data-dock-keep-open="true"
            className={mapDockItemClass("help", alertOpen || hasUnreadAlert)}
            onClick={() => {
              setAlertOpen((v) => {
                mapDockOverlayOpenRef.current = !v;
                return !v;
              });
              if (canViewAlerts && activeEventId) {
                localStorage.setItem(`liveAlerts.${activeEventId}.lastRead`, String(Date.now()));
                setHasUnreadAlert(false);
              }
            }}
            style={{
              ...labeledMapBubbleStyle,
              background:
                alertOpen || hasUnreadAlert
                  ? "rgba(40,167,69,0.18)"
                  : trekkinBubbleStyle.background,
              boxShadow: alertOpen
                ? "0 0 0 2px rgba(40,167,69,0.35)"
                : trekkinBubbleStyle.boxShadow,
            }}
          >
            {chatUnreadCount > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  minWidth: 18,
                  height: 18,
                  padding: "0 4px",
                  boxSizing: "border-box",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 999,
                  border: "2px solid #fff",
                  background: "#e32636",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: 1,
                  zIndex: 2,
                }}
              >
                {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
              </span>
            )}
            <img
              src={`${base}icons/alert.svg`}
              alt="Alertar guia"
              style={labeledMapBubbleIcon}
            />
            <span style={mapBubbleLabelStyle}>{translate("topBar.help")}</span>
          </button>

          {alertOpen && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: "110%",
                background: "rgba(255,255,255,0.95)",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 12,
                padding: 8,
                minWidth: 200,
                boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {canViewAlerts ? (
                liveAlerts.length ? (
                  liveAlerts
                    .slice()
                    .reverse()
                    .map((a, idx) => (
                      <div
                        key={`${a.uid || "u"}-${a.ts || idx}`}
                        style={{
                          background: "rgba(40,167,69,0.08)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          border: "1px solid rgba(40,167,69,0.2)",
                          fontSize: 13,
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#2c6b3f" }}>
                          {a.name || "Utilizador"}
                        </div>
                        <div>{a.label || "Alerta"}</div>
                        {a.ts ? (
                          <div style={{ fontSize: 11, color: "#666" }}>
                            {new Date(a.ts).toLocaleTimeString()}
                          </div>
                        ) : null}
                      </div>
                    ))
                ) : (
                  <div style={{ fontSize: 13, color: "#666" }}>
                    — Sem alertas —
                  </div>
                )
              ) : (
                [
                  { label: "Preciso de descanso.", toast: "O Guia foi alertado!" },
                  { label: "Preciso de me alimentar.", toast: "O Guia foi alertado!" },
                  { label: "Sinto-me mal.", toast: "Aguarde a chegada do Guia." },
                  { label: "Magoei-me.", toast: "Aguarde a chegada do Guia." },
                  { label: "Necessidades.", toast: "O Guia foi alertado, deixe o grupo passar." },
                ].map((item) => (
                  <TrekkinButton
                    key={item.label}
                    onClick={() => sendLiveAlert(item.label, item.toast)}
                    style={{ justifyContent: "flex-start", fontSize: 13, padding: "8px 10px" }}
                  >
                    {item.label}
                  </TrekkinButton>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* 💬 CHAT */}
      {mapDockOpen && !isFullscreen && (
        <>
          <button
            type="button"
            data-dock-item="chat"
            className={mapDockItemClass("chat", hasUnread)}
            onClick={handleOpenMessages}
            aria-label={hasUnread ? "Chat — novas mensagens" : "Chat"}
            style={{
              ...labeledMapBubbleStyle,
              position: "relative",
              background: hasUnread
                ? "#28a745"
                : trekkinBubbleStyle.background,
              boxShadow: hasUnread
                ? "0 0 0 2px rgba(40,167,69,0.45)"
                : trekkinBubbleStyle.boxShadow,
              animation: hasUnread
                ? "chatPulse 1.5s infinite ease-out"
                : "none",
            }}
          >
            <img
              src={`${base}icons/new-message.svg`}
              alt="Chat"
              style={{
                ...labeledMapBubbleIcon,
                filter: hasUnread
                  ? "brightness(1.25) drop-shadow(0 0 4px rgba(255,255,255,0.6))"
                  : "none",
              }}
            />
            <span style={mapBubbleLabelStyle}>{translate("topBar.chat")}</span>
          </button>

          {unreadChatPickerOpen && typeof document !== "undefined" && document.body
            ? createPortal(
                <div
                  className="tk-top-anchored-overlay"
                  role="presentation"
                  onPointerDown={(event) => {
                    if (event.target === event.currentTarget) {
                      setUnreadChatPickerOpen(false);
                    }
                  }}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 99990,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))",
                    background: "rgba(0,0,0,0.28)",
                    pointerEvents: "auto",
                  }}
                >
                  <div
                    className="tk-top-anchored-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="unread-chat-picker-title"
                    style={{
                      width: "min(420px, calc(100vw - 32px))",
                      maxHeight: "min(620px, calc(100dvh - 80px))",
                      background: "rgba(255,255,255,0.98)",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 16,
                      padding: 12,
                      boxShadow: "0 12px 36px rgba(0,0,0,0.28)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      color: "#111",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div id="unread-chat-picker-title" style={{ fontWeight: 800, fontSize: 17 }}>
                          Novas mensagens
                        </div>
                        <div style={{ color: "#666", fontSize: 13, marginTop: 2 }}>
                          Escolhe o chat que queres abrir
                        </div>
                      </div>
                      <CloseButton onClick={() => setUnreadChatPickerOpen(false)} />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 7, overflowY: "auto" }}>
                      {unreadChatOptions.map((option) => (
                        <button
                          key={`${option.type}:${option.id}`}
                          type="button"
                          onClick={() => openUnreadChat(option)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            width: "100%",
                            padding: "11px 12px",
                            border: "1px solid rgba(40,167,69,0.28)",
                            borderRadius: 12,
                            background: "rgba(40,167,69,0.09)",
                            color: "#183d22",
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          <img src={`${base}icons/new-message.svg`} alt="" style={{ width: 25, height: 25 }} />
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <span style={{ display: "block", fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {option.label}
                            </span>
                            {option.subtitle ? (
                              <span style={{ display: "block", marginTop: 2, color: "#5d6b61", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {option.subtitle}
                              </span>
                            ) : null}
                          </span>
                          <span aria-hidden="true" style={{ color: "#28a745", fontSize: 22 }}>›</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </>
      )}

      {/* 🎯 CENTRAR */}
      {mapDockOpen && !isFullscreen && (
        <button
          type="button"
          data-dock-item="center"
          className={mapDockItemClass("center", followEnabled)}
          onClick={async () => {
            try {
              const locationResult = await ensureLocationPermissionForCenter();
              if (!locationResult?.granted) {
                showToast("⚠️ Permite localização para centrar o mapa.");
                return;
              }

              const freshPosition = locationResult?.position || null;
              if (freshPosition) {
                // A gravação pode ter terminado o watcher que alimentava o
                // marcador. Não deitamos fora este fix pedido pelo próprio botão:
                // promovemo-lo a posição atual antes de centrar o mapa.
                usePanelsStore.getState().setUserPosition(freshPosition);
              }

              const next = !followEnabled;
              setFollowEnabled(next);
              window.dispatchEvent(
                new CustomEvent("trekkin:set-follow-enabled", {
                  detail: { enabled: next },
                })
              );
              // "Centrar" deve sempre aplicar a posição obtida, mesmo quando o
              // toque está a desligar o seguimento automático (botão já verde).
              window.dispatchEvent(
                new CustomEvent("trekkin:center-map", {
                  detail: freshPosition
                    ? {
                        lat: freshPosition.lat,
                        lon: freshPosition.lon,
                        ele: freshPosition.ele,
                        acc: freshPosition.acc,
                        spd: freshPosition.spd,
                        ts: freshPosition.ts,
                        source: freshPosition.source,
                        zoom: 17,
                      }
                    : undefined,
                })
              );
            } catch (err) {
              console.warn("⚠️ Falha ao centrar mapa:", err);
              showToast("Não foi possível centrar o mapa.");
            }
          }}
          style={{
            ...labeledMapBubbleStyle,
            background: followEnabled
              ? "rgba(40,167,69,0.2)"
              : trekkinBubbleStyle.background,
            boxShadow: followEnabled
              ? "0 0 0 2px rgba(40,167,69,0.35)"
              : trekkinBubbleStyle.boxShadow,
          }}
        >
          <img
            src={`${base}icons/location.svg`}
            alt="Centrar"
            style={{
              ...labeledMapBubbleIcon,
              filter: followEnabled
                ? "drop-shadow(0 0 6px #28a745)"
                : "none",
            }}
          />
          <span style={mapBubbleLabelStyle}>{translate("topBar.center")}</span>
        </button>
      )}

      {/* 🧭 REDE DE NAVEGAÇÃO */}
      {mapDockOpen && !isFullscreen && canNavGrid && (
        <button
          type="button"
          data-dock-item="network"
          className={mapDockItemClass("network", navGridOn)}
          onClick={() => {
            const next = !navGridOn;
            const isAndroidRuntime =
              String(window?.Capacitor?.getPlatform?.() || "").toLowerCase() ===
                "android" || /Android/i.test(navigator.userAgent || "");
            const processingId =
              next && isAndroidRuntime
                ? `navgrid-layer-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 8)}`
                : null;
            if (processingId) {
              window.dispatchEvent(
                new CustomEvent("trekkin:processing", {
                  detail: {
                    active: true,
                    id: processingId,
                    text: "A preparar a rede de navegação...",
                    minimumMs: 650,
                  },
                })
              );
              window.setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("trekkin:processing", {
                    detail: { active: false, id: processingId },
                  })
                );
              }, 8000);
            }
            setNavGridOn(next);
            try {
              localStorage.setItem("navGridLayerOn", next ? "1" : "0");
            } catch {}
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-toggle", {
                detail: { on: next, processingId },
              })
            );
          }}
          style={{
            ...labeledMapBubbleStyle,
            background: navGridOn
              ? "rgba(40,167,69,0.2)"
              : trekkinBubbleStyle.background,
          }}
        >
          <img
            src={`${base}icons/grid.svg`}
            alt="Rede"
            style={labeledMapBubbleIcon}
          />
          <span style={mapBubbleLabelStyle}>{translate("topBar.network")}</span>
        </button>
      )}

      {/* 🏞️ REGIMES DE PROTEÇÃO DO PNPG */}
      {mapDockOpen && !isFullscreen && (
        <button
          type="button"
          data-dock-item="protected-zones"
          className={mapDockItemClass("protected-zones", protectedZonesOn)}
          onClick={() => {
            const next = !protectedZonesOn;
            setProtectedZonesOn(next);
            try {
              localStorage.setItem("protectedZonesLayerOn", next ? "1" : "0");
            } catch {}
            window.dispatchEvent(
              new CustomEvent("trekkin:protected-zones-toggle", {
                detail: { on: next, areaId: "pnpg" },
              })
            );
          }}
          style={{
            ...labeledMapBubbleStyle,
            background: protectedZonesOn
              ? "rgba(40,167,69,0.24)"
              : trekkinBubbleStyle.background,
            boxShadow: protectedZonesOn
              ? "0 0 0 2px rgba(40,167,69,0.38), 0 0 12px rgba(40,167,69,0.5)"
              : trekkinBubbleStyle.boxShadow,
          }}
          aria-pressed={protectedZonesOn}
        >
          <img src={`${base}icons/park.svg`} alt="" style={labeledMapBubbleIcon} />
          <span style={mapBubbleLabelStyle}>{translate("topBar.zones")}</span>
        </button>
      )}

      {/* ⏹️ TERMINAR GRAVAÇÃO DA REDE */}
      {mapDockOpen && !isFullscreen && canNavGrid && navGridRecordActive && (
        <button
          type="button"
          data-dock-item="finish-record"
          className={mapDockItemClass("finish-record", navGridRecordActive)}
          onClick={() => {
            window.dispatchEvent(
              new CustomEvent("trekkin:navgrid-record-toggle", {
                detail: { active: false },
              })
            );
            openPanel("adminNavGrid", { from: "recording_finished" });
          }}
          style={{
            ...labeledMapBubbleStyle,
            background: "rgba(220,53,69,0.2)",
            boxShadow: "0 0 0 2px rgba(220,53,69,0.4)",
          }}
          aria-label="Terminar gravação do trecho"
        >
          <img
            src={`${base}icons/stop-record.svg`}
            alt=""
            style={labeledMapBubbleIcon}
          />
          <span style={mapBubbleLabelStyle}>Terminar</span>
        </button>
      )}

      {/* 📍 WAYPOINTS */}
      {mapDockOpen && !isFullscreen && canWaypoints && (
        <button
          type="button"
          data-dock-item="points"
          className={mapDockItemClass("points", waypointsLayerOn)}
          onPointerDown={startWaypointsLongPress}
          onPointerMove={moveWaypointsLongPress}
          onPointerUp={cancelWaypointsLongPress}
          onPointerCancel={cancelWaypointsLongPress}
          onPointerLeave={cancelWaypointsLongPress}
          onTouchStart={startWaypointsTouchLongPress}
          onTouchMove={moveWaypointsTouchLongPress}
          onTouchEnd={cancelWaypointsLongPress}
          onTouchCancel={cancelWaypointsLongPress}
          onContextMenu={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            if (suppressWaypointsClickRef.current) {
              suppressWaypointsClickRef.current = false;
              return;
            }
            const next = !waypointsLayerOn;
            const isAndroidRuntime =
              String(window?.Capacitor?.getPlatform?.() || "").toLowerCase() ===
                "android" || /Android/i.test(navigator.userAgent || "");
            const processingId =
              next && isAndroidRuntime
                ? `waypoints-layer-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 8)}`
                : null;
            if (processingId) {
              window.dispatchEvent(
                new CustomEvent("trekkin:processing", {
                  detail: {
                    active: true,
                    id: processingId,
                    text: "A carregar os waypoints no mapa...",
                    minimumMs: 650,
                  },
                })
              );
              window.setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent("trekkin:processing", {
                    detail: { active: false, id: processingId },
                  })
                );
              }, 8000);
            }
            setWaypointsLayerOn(next);
            let categories = [];
            let categoryPalette = {};
            try {
              const rawCats = localStorage.getItem("waypointsSelectedCategories");
              const parsedCats = rawCats ? JSON.parse(rawCats) : [];
              categories = Array.isArray(parsedCats) ? parsedCats : [];
            } catch {}
            try {
              const rawPalette = localStorage.getItem("waypointsCategoryPalette");
              const parsedPalette = rawPalette ? JSON.parse(rawPalette) : {};
              categoryPalette =
                parsedPalette && typeof parsedPalette === "object"
                  ? parsedPalette
                  : {};
            } catch {}
            try {
              localStorage.setItem("waypointsLayerOn", next ? "1" : "0");
            } catch {}
            window.dispatchEvent(
              new CustomEvent("trekkin:waypoints-map-filter", {
                detail: {
                  on: next,
                  categories,
                  categoryPalette,
                  processingId,
                },
              })
            );
          }}
          style={{
            ...labeledMapBubbleStyle,
            background: waypointsLayerOn
              ? "rgba(40,167,69,0.2)"
              : trekkinBubbleStyle.background,
          }}
        >
          <img
            src={`${base}icons/waypoints.svg`}
            alt="Waypoints"
            draggable="false"
            onDragStart={(event) => event.preventDefault()}
            onContextMenu={(event) => event.preventDefault()}
            style={{
              ...labeledMapBubbleIcon,
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
          <span style={mapBubbleLabelStyle}>{translate("topBar.points")}</span>
        </button>
      )}

      {/* 🚨 ALERTAS (MAPA) */}
      {mapDockOpen && !isFullscreen && canUseAlertsMenu && (
        <div style={{ position: "relative" }}>
          <button
            type="button"
            data-dock-item="alerts"
            data-dock-keep-open="true"
            className={mapDockItemClass(
              "alerts",
              recentAlerts.length > 0
            )}
            onClick={() => {
              setAlertsMenuOpen((v) => {
                mapDockOverlayOpenRef.current = !v;
                return !v;
              });
              if (canViewMapAlerts || canViewTargetedAlerts) {
                localStorage.setItem("mapAlerts.lastRead", String(Date.now()));
                setHasUnreadMapAlert(false);
              }
            }}
            style={{
              ...labeledMapBubbleStyle,
              background: hasUnreadMapAlert
                ? "rgba(40,167,69,0.22)"
                : trekkinBubbleStyle.background,
              boxShadow: hasUnreadMapAlert
                ? "0 0 10px rgba(40,167,69,0.8), 0 0 18px rgba(40,167,69,0.55)"
                : trekkinBubbleStyle.boxShadow,
            }}
          >
            <img
              src={`${base}icons/warning.svg`}
              alt="Alertas"
              style={labeledMapBubbleIcon}
            />
            <span style={mapBubbleLabelStyle}>{translate("topBar.alerts")}</span>
          </button>

          {alertsMenuOpen && typeof document !== "undefined" && document.body
            ? createPortal(
                <div
                  className="tk-top-anchored-overlay"
                  onPointerDown={(event) => {
                    if (event.target === event.currentTarget) {
                      setAlertsMenuOpen(false);
                    }
                  }}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 99990,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))",
                    pointerEvents: "auto",
                  }}
                >
                  <div
                    className="tk-top-anchored-panel"
                    style={{
                      width: "min(420px, calc(100vw - 32px))",
                      maxHeight: "min(760px, calc(100dvh - 80px))",
                      background: "rgba(255,255,255,0.96)",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 12,
                      padding: 10,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.24)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      pointerEvents: "auto",
                      overflow: "hidden",
                      fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif",
                      color: "#111",
                    }}
                  >
                    <header className="tk-modal-standard-hero tk-alerts-menu-hero">
                      <div className="tk-standard-panel-close"><CloseButton onClick={() => setAlertsMenuOpen(false)} /></div>
                      <div className="tk-standard-panel-brand"><img src={`${base}icons/logo.png`} alt=""/><strong>Trekkin</strong></div>
                      <h3>Alertas</h3>
                      <p>Consulta notificações e alertas recentes.</p>
                    </header>
                    {(canViewMapAlerts || canViewTargetedAlerts) && (
                      <div
                        style={{
                          minHeight: 0,
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {recentAlerts.length ? (
                          recentAlerts
                            .slice()
                            .reverse()
                            .map((a, idx) => (
                              <div
                                key={`${a.id || a.ts || idx}`}
                                onClick={() => {
                                  if (a.source === "event_invite_request" && a.eventId) {
                                    setAlertsMenuOpen(false);
                                    openPanel("eventManage", { eventId: a.eventId });
                                    return;
                                  }
                                  if (a.source === "event_invite_request_decision" && a.eventId) {
                                    setAlertsMenuOpen(false);
                                    openPanel("eventDetails", { eventId: a.eventId });
                                    return;
                                  }
                                  if (String(a.source || "").startsWith("moderation_report_")) {
                                    setAlertsMenuOpen(false);
                                    openPanel("myIssueReports");
                                    return;
                                  }
                                  setAlertsMenuOpen(false);
                                  if (a.source === "issue_report" && a.reportId) {
                                    openPanel("adminReports", { selectedReportId: a.reportId });
                                    return;
                                  }
                                  openPanel("alertsAdmin", {
                                    selectedAlert: {
                                      id: a.id || null,
                                      category: a.category || null,
                                      ts: Number(a.ts || 0),
                                      type: a.type || "",
                                    },
                                  });
                                }}
                                style={{
                                  background: "rgba(40,167,69,0.08)",
                                  borderRadius: 10,
                                  padding: "8px 10px",
                                  border: "1px solid rgba(40,167,69,0.2)",
                                  fontSize: 13,
                                  cursor: "pointer",
                                  flex: "none",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 48 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 800, color: "#111" }}>
                                      {getAlertLabel(a)}
                                    </div>
                                    {String(a.source || "").startsWith("moderation_report_") && a.detail ? (
                                      <div style={{ marginTop: 3, fontSize: 12, color: "#444" }}>
                                        {a.detail}
                                      </div>
                                    ) : null}
                                    {a.ts ? (
                                      <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: "#111" }}>
                                        {new Date(a.ts).toLocaleTimeString()}
                                      </div>
                                    ) : null}
                                  </div>
                                  {!String(a.source || "").startsWith("moderation_report_") && (
                                    <TrekkinButton
                                      square
                                      icon="send.svg"
                                      size={22}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleSendAlertMail(a);
                                      }}
                                      style={{ width: 44, height: 44, borderRadius: 9, alignSelf: "center" }}
                                    />
                                  )}
                                  {isAdminUser && canManageAlerts && (
                                    <TrekkinButton
                                      square
                                      icon="trash.svg"
                                      title={String(a.category).toLowerCase() === "fauna" ? "Arquivar sem apagar" : "Eliminar"}
                                      size={22}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeleteRecentAlert(a);
                                      }}
                                      style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 9,
                                        alignSelf: "center",
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            ))
                        ) : (
                          <div style={{ fontSize: 13, color: "#666", padding: 8 }}>
                            — Sem alertas —
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: "none" }}>
                      {canViewMapAlerts && (
                        <TrekkinButton
                          onClick={handleViewMapAlerts}
                          style={{ justifyContent: "flex-start", fontSize: 13, fontWeight: 600, color: "#111", padding: "8px 10px" }}
                        >
                          {alertsLayerOn ? "Remover alertas do mapa" : "Ver alertas no mapa"}
                        </TrekkinButton>
                      )}

                      {canAddAlerts && (
                        <TrekkinButton
                          onClick={handleCreateAlert}
                          style={{ justifyContent: "flex-start", fontSize: 13, fontWeight: 600, color: "#111", padding: "8px 10px" }}
                        >
                          Criar novo alerta
                        </TrekkinButton>
                      )}

                      {isAdminUser && canManageAlerts && (
                        <TrekkinButton
                          onClick={handleOpenAlertsPanel}
                          style={{ justifyContent: "flex-start", fontSize: 13, fontWeight: 600, color: "#111", padding: "8px 10px" }}
                        >
                          Painel de alertas
                        </TrekkinButton>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>
      )}

      {/* Sempre na última posição da barra. */}
      {mapDockOpen && !isFullscreen && mapOnlyTrails.length > 0 && (
        <button
          type="button"
          data-dock-item="clear-trails"
          className={mapDockItemClass("clear-trails")}
          onClick={() => {
            if (mapOnlyTrails.length === 1) {
              clearMapOnlyTrail(mapOnlyTrails[0]?.trailId);
            } else {
              setTrailClearModalOpen(true);
            }
          }}
          style={labeledMapBubbleStyle}
          aria-label={translate("topBar.clearTrails")}
          title={translate("topBar.clearTrails")}
        >
          <img src={`${base}icons/trail.svg`} alt="" style={labeledMapBubbleIcon} />
          <span style={mapBubbleLabelStyle}>{translate("topBar.clear")}</span>
        </button>
      )}

      {/* Sempre no fim da TopControlBar durante a gravação. */}
      {mapDockOpen && !isFullscreen && <HeartRateMapHUD embedded />}

      {trailClearModalOpen && mapOnlyTrails.length > 1
        ? createPortal(
            <div
              className="tk-top-anchored-overlay"
              role="presentation"
              onClick={() => setTrailClearModalOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 12000,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <div
                className="tk-top-anchored-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Trilhos visíveis"
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: "min(680px, 100%)",
                  maxHeight: "85vh",
                  overflowY: "auto",
                  background: "rgba(255,255,255,0.98)",
                  borderRadius: 20,
                  padding: "18px 18px 16px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  color: "#111",
                  position: "relative",
                }}
              >
                <div style={{ position: "absolute", top: 12, right: 12 }}>
                  <CloseButton onClick={() => setTrailClearModalOpen(false)} />
                </div>
                <h3
                  style={{
                    margin: "0 42px 6px 0",
                    color: "#28a745",
                    fontSize: 28,
                    fontWeight: 800,
                  }}
                >
                  Trilhos visíveis
                </h3>
                <div style={{ fontSize: 14, color: "#5b6470", marginBottom: 14 }}>
                  Escolhe o trilho que pretendes ocultar do mapa.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {mapOnlyTrails.map((item, index) => (
                    <button
                      key={item.trailId}
                      type="button"
                      onClick={() => clearMapOnlyTrail(item.trailId)}
                      style={{
                        minHeight: 54,
                        border: "1px solid rgba(40,167,69,0.14)",
                        borderRadius: 14,
                        background: "#f4fbf5",
                        color: "#1f2d3d",
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        textAlign: "left",
                        fontSize: 15,
                        fontWeight: 800,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 14,
                          height: 14,
                          flex: "0 0 auto",
                          borderRadius: 999,
                          background: trailPreviewPalette[index % trailPreviewPalette.length],
                        }}
                      />
                      <span style={{ flex: 1 }}>{item.trailName || "Trilho"}</span>
                      <span
                        style={{
                          borderRadius: 999,
                          background: "#e9ecef",
                          color: "#1f2d3d",
                          padding: "7px 12px",
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        Ocultar
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => clearMapOnlyTrail("")}
                  style={{
                    width: "100%",
                    minHeight: 48,
                    marginTop: 12,
                    border: "none",
                    borderRadius: 999,
                    background: "#d9534f",
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 800,
                  }}
                >
                  Limpar todos
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
